/* eslint-disable max-lines */
import {
    useState, useEffect
} from 'react';
import {
    Button
} from 'shared/components/ui/button';
import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    Checkbox
} from 'shared/components/ui/checkbox';
import {
    Plus, Trash2, ChevronDown, ChevronRight
} from 'lucide-react';
import {
    JsonSchema, JsonSchemaProperty, JsonSchemaType, SchemaField
} from '../types';
import {
    schemaExamples
} from '../SchemaExamples';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from 'shared/components/ui/dropdown-menu';

interface JSONSchemaBuilderProps {
    initialSchema?: string;
    onChange: (schema: string) => void;
    title?: string;
}

const JSON_TYPES: JsonSchemaType[] = ['string', 'number', 'boolean', 'object', 'array', 'null'];

export function JSONSchemaBuilder({
    initialSchema, onChange, title = 'JSON Schema Builder'
}: JSONSchemaBuilderProps) {
    const [rootType, setRootType] = useState<JsonSchemaType>('object');
    const [fields, setFields] = useState<SchemaField[]>([]);
    const [jsonPreview, setJsonPreview] = useState<string>('');
    const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
    // Editable text for the right-hand preview (paste target) + parse status.
    const [editText, setEditText] = useState<string>('');
    const [parseError, setParseError] = useState<string | null>(null);

    // Initialize from existing schema
    useEffect(() => {
        if (initialSchema && initialSchema.trim()) {
            try {
                const parsed: JsonSchema = JSON.parse(initialSchema);
                loadSchemaIntoBuilder(parsed);
            } catch (error) {
                console.error('Error parsing initial schema:', error);
            }
        }
    }, []);

    // Update JSON preview whenever fields or rootType change
    useEffect(() => {
        const schema = buildJsonSchema();
        const schemaString = JSON.stringify(schema, null, 2);
        setJsonPreview(schemaString);
        onChange(schemaString);
    }, [fields, rootType]);

    // Keep the editable preview in sync whenever the builder regenerates the schema.
    useEffect(() => {
        setEditText(jsonPreview);
        setParseError(null);
    }, [jsonPreview]);

    // Infer a JSON Schema from a raw example value (object / array / scalar).
    const inferSchema = (value: unknown): JsonSchema => {
        if (value === null) {
            return {
                type: 'null'
            };
        }
        if (Array.isArray(value)) {
            const first = value.length > 0 ? value[0] : null;
            const itemSchema = first === null ? {
                type: 'string'
            } : inferSchema(first);
            return {
                type: 'array',
                items: itemSchema as JsonSchemaProperty,
            };
        }
        const t = typeof value;
        if (t === 'object') {
            const properties: Record<string, JsonSchemaProperty> = {};
            Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
                properties[k] = inferSchema(v) as JsonSchemaProperty;
            });
            return {
                type: 'object',
                properties,
                required: Object.keys(value as object),
            };
        }
        if (t === 'number') {
            return {
                type: 'number'
            };
        }
        if (t === 'boolean') {
            return {
                type: 'boolean'
            };
        }
        return {
            type: 'string'
        };
    };

    // User typed/pasted into the preview. Parse it; load into the builder when valid.
    // Accepts a real JSON Schema, or a raw example JSON (schema is inferred from it).
    const handlePreviewChange = (text: string) => {
        setEditText(text);
        if (!text.trim()) {
            setParseError(null);
            return;
        }
        let parsed: unknown;
        try {
            parsed = JSON.parse(text);
        } catch {
            setParseError('Invalid JSON — fix it to apply to the builder');
            return;
        }
        setParseError(null);
        const obj = parsed as { type?: unknown; properties?: unknown; items?: unknown };
        const looksLikeSchema = !!obj
            && typeof obj === 'object'
            && !Array.isArray(parsed)
            && typeof obj.type === 'string'
            && JSON_TYPES.includes(obj.type as JsonSchemaType)
            && ('properties' in obj || 'items' in obj
                || ['string', 'number', 'boolean', 'null'].includes(obj.type as string));
        loadSchemaIntoBuilder(looksLikeSchema ? (parsed as JsonSchema) : inferSchema(parsed));
    };

    const loadSchemaIntoBuilder = (schema: JsonSchema) => {
        setRootType(schema.type);

        if (schema.type === 'object' && schema.properties) {
            const loadedFields = loadPropertiesAsFields(schema.properties, schema.required || []);
            setFields(loadedFields);
        } else if (schema.type === 'array' && schema.items) {
            if (schema.items.type === 'object' && schema.items.properties) {
                const loadedFields = loadPropertiesAsFields(
                    schema.items.properties,
                    schema.items.required || []
                );
                setFields(loadedFields);
            }
        }
    };

    const loadPropertiesAsFields = (
        properties: Record<string, JsonSchemaProperty>,
        required: string[]
    ): SchemaField[] => {
        return Object.entries(properties).map(([key, value]) => {
            const field: SchemaField = {
                id: Math.random().toString(36).substr(2, 9),
                name: key,
                type: value.type,
                required: required.includes(key),
                defaultValue: value.default,
                description: value.description,
            };

            // Handle nested object
            if (value.type === 'object' && value.properties) {
                field.children = loadPropertiesAsFields(value.properties, value.required || []);
            }

            // Handle array with object items
            if (value.type === 'array' && value.items) {
                field.arrayItemType = value.items.type;
                if (value.items.type === 'object' && value.items.properties) {
                    field.arrayItemProperties = loadPropertiesAsFields(
                        value.items.properties,
                        value.items.required || []
                    );
                }
            }

            return field;
        });
    };

    const buildJsonSchema = (): JsonSchema => {
        if (rootType === 'object') {
            return {
                type: 'object',
                properties: buildProperties(fields),
                required: fields.filter(f => f.required && f.name.trim()).map(f => f.name),
            };
        } else if (rootType === 'array') {
            const itemProperties = buildProperties(fields);
            const itemRequired = fields.filter(f => f.required && f.name.trim()).map(f => f.name);

            return {
                type: 'array',
                items: {
                    type: 'object',
                    properties: itemProperties,
                    ...(itemRequired.length > 0 && {
                        required: itemRequired
                    }),
                },
            };
        } else {
            return {
                type: rootType,
            };
        }
    };

    const buildProperties = (fieldsList: SchemaField[]): Record<string, JsonSchemaProperty> => {
        const properties: Record<string, JsonSchemaProperty> = {};

        fieldsList.forEach((field) => {
            if (field.name.trim()) {
                const property: JsonSchemaProperty = {
                    type: field.type,
                };

                // Add default value if present
                if (field.defaultValue !== undefined && field.defaultValue !== null && field.defaultValue !== '') {
                    property.default = field.defaultValue;
                }

                // Add description if present
                if (field.description) {
                    property.description = field.description;
                }

                // Handle nested object
                if (field.type === 'object' && field.children && field.children.length > 0) {
                    property.properties = buildProperties(field.children);
                    const childRequired = field.children.filter(c => c.required && c.name.trim()).map(c => c.name);
                    if (childRequired.length > 0) {
                        property.required = childRequired;
                    }
                }

                // Handle array
                if (field.type === 'array') {
                    if (field.arrayItemType === 'object' && field.arrayItemProperties && field.arrayItemProperties.length > 0) {
                        const itemProps = buildProperties(field.arrayItemProperties);
                        const itemRequired = field.arrayItemProperties.filter(p => p.required && p.name.trim()).map(p => p.name);

                        property.items = {
                            type: 'object',
                            properties: itemProps,
                            ...(itemRequired.length > 0 && {
                                required: itemRequired
                            }),
                        };
                    } else if (field.arrayItemType && field.arrayItemType !== 'object') {
                        property.items = {
                            type: field.arrayItemType,
                        };
                    }
                }

                properties[field.name] = property;
            }
        });

        return properties;
    };

    const addField = (parentPath?: string) => {
        const newField: SchemaField = {
            id: Math.random().toString(36).substr(2, 9),
            name: '',
            type: 'string',
            required: false,
        };

        if (!parentPath) {
            setFields([...fields, newField]);
        } else {
            setFields(updateNestedFields(fields, parentPath, (children) => [...children, newField]));
        }
    };

    const removeField = (id: string, parentPath?: string) => {
        if (!parentPath) {
            setFields(fields.filter((field) => field.id !== id));
        } else {
            setFields(updateNestedFields(fields, parentPath, (children) =>
                children.filter(f => f.id !== id)
            ));
        }
    };

    const updateField = (id: string, updates: Partial<SchemaField>, parentPath?: string) => {
        const updateFn = (fieldsList: SchemaField[]): SchemaField[] => {
            return fieldsList.map((field) => {
                if (field.id === id) {
                    const updatedField = {
                        ...field,
                        ...updates
                    };

                    // When type changes to object, initialize children if not present
                    if (updates.type === 'object' && !updatedField.children) {
                        updatedField.children = [];
                    }

                    // When type changes to array, initialize array properties
                    if (updates.type === 'array' && !updatedField.arrayItemType) {
                        updatedField.arrayItemType = 'string';
                    }

                    // When type changes away from object, clear children
                    if (updates.type && updates.type !== 'object') {
                        updatedField.children = undefined;
                    }

                    // When type changes away from array, clear array properties
                    if (updates.type && updates.type !== 'array') {
                        updatedField.arrayItemType = undefined;
                        updatedField.arrayItemProperties = undefined;
                    }

                    return updatedField;
                }
                return field;
            });
        };

        if (!parentPath) {
            setFields(updateFn(fields));
        } else {
            setFields(updateNestedFields(fields, parentPath, updateFn));
        }
    };

    const updateNestedFields = (
        fieldsList: SchemaField[],
        path: string,
        updateFn: (children: SchemaField[]) => SchemaField[]
    ): SchemaField[] => {
        const [currentId, ...restPath] = path.split('.');

        return fieldsList.map(field => {
            if (field.id === currentId) {
                if (restPath.length === 0) {
                    // We're at the target field
                    if (field.children) {
                        return {
                            ...field,
                            children: updateFn(field.children)
                        };
                    } else if (field.arrayItemProperties) {
                        return {
                            ...field,
                            arrayItemProperties: updateFn(field.arrayItemProperties)
                        };
                    }
                } else {
                    // Continue traversing
                    if (field.children) {
                        return {
                            ...field,
                            children: updateNestedFields(field.children, restPath.join('.'), updateFn)
                        };
                    } else if (field.arrayItemProperties) {
                        return {
                            ...field,
                            arrayItemProperties: updateNestedFields(field.arrayItemProperties, restPath.join('.'), updateFn)
                        };
                    }
                }
            }
            return field;
        });
    };

    const loadExample = (exampleKey: string) => {
        const schema = schemaExamples[exampleKey];
        loadSchemaIntoBuilder(schema);
    };

    const clearSchema = () => {
        setFields([]);
        setRootType('object');
        setExpandedFields(new Set());
    };

    const handleDefaultValueChange = (id: string, value: string, parentPath?: string) => {
        const findField = (fieldsList: SchemaField[]): SchemaField | undefined => {
            for (const field of fieldsList) {
                if (field.id === id) return field;
                if (field.children) {
                    const found = findField(field.children);
                    if (found) return found;
                }
                if (field.arrayItemProperties) {
                    const found = findField(field.arrayItemProperties);
                    if (found) return found;
                }
            }
            return undefined;
        };

        const field = findField(fields);
        if (!field) return;

        let parsedValue: string | number | boolean | null | undefined = value;

        if (field.type === 'number') {
            parsedValue = value === '' ? undefined : parseFloat(value);
        } else if (field.type === 'boolean') {
            if (value === 'undefined') {
                parsedValue = undefined;
            } else {
                parsedValue = value === 'true';
            }
        } else if (field.type === 'null') {
            parsedValue = null;
        } else if (value === '') {
            parsedValue = undefined;
        }

        updateField(id, {
            defaultValue: parsedValue
        }, parentPath);
    };

    const toggleExpanded = (fieldId: string) => {
        const newExpanded = new Set(expandedFields);
        if (newExpanded.has(fieldId)) {
            newExpanded.delete(fieldId);
        } else {
            newExpanded.add(fieldId);
        }
        setExpandedFields(newExpanded);
    };

    const renderFields = (fieldsList: SchemaField[], parentPath = '', depth = 0) => {
        return fieldsList.map((field) => {
            const currentPath = parentPath ? `${parentPath}.${field.id}` : field.id;
            const isExpanded = expandedFields.has(currentPath);

            return (
                <div key={field.id} className="space-y-3">
                    <div
                        className="border rounded-md p-3 space-y-3"
                        style={{
                            marginLeft: `${depth * 16}px`,
                            borderLeft: depth > 0 ? '3px solid hsl(var(--primary))' : undefined
                        }}
                    >
                        <div className="flex gap-2 items-start">
                            {/* Expand/Collapse for nested fields */}
                            {(field.type === 'object' || field.type === 'array') && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-9 w-9 mt-5"
                                    onClick={() => toggleExpanded(currentPath)}
                                >
                                    {isExpanded ? (
                                        <ChevronDown className="h-4 w-4" />
                                    ) : (
                                        <ChevronRight className="h-4 w-4" />
                                    )}
                                </Button>
                            )}

                            {/* Field Name */}
                            <div className="flex-1">
                                <Label className="text-xs">Field Name</Label>
                                <Input
                                    value={field.name}
                                    onChange={(e) => updateField(field.id, {
                                        name: e.target.value
                                    }, parentPath)}
                                    placeholder="fieldName"
                                    className="mt-1"
                                />
                            </div>

                            {/* Field Type */}
                            <div className="flex-1">
                                <Label className="text-xs">Type</Label>
                                <Select
                                    value={field.type}
                                    onValueChange={(value) => {
                                        const newType = value as JsonSchemaType;
                                        const updates: Partial<SchemaField> = {
                                            type: newType
                                        };

                                        if (newType === 'object') {
                                            updates.children = [];
                                            toggleExpanded(currentPath);
                                        } else if (newType === 'array') {
                                            updates.arrayItemType = 'string';
                                        }

                                        updateField(field.id, updates, parentPath);
                                    }}
                                >
                                    <SelectTrigger className="mt-1">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {JSON_TYPES.map((type) => (
                                            <SelectItem key={type} value={type}>
                                                {type}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Delete Button */}
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeField(field.id, parentPath)}
                                className="mt-5 h-9 w-9 text-destructive hover:text-destructive"
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>

                        {/* Array Item Type Selector */}
                        {field.type === 'array' && isExpanded && (
                            <div>
                                <Label className="text-xs">Array Item Type</Label>
                                <Select
                                    value={field.arrayItemType || 'string'}
                                    onValueChange={(value) => {
                                        const itemType = value as JsonSchemaType;
                                        const updates: Partial<SchemaField> = {
                                            arrayItemType: itemType
                                        };

                                        if (itemType === 'object') {
                                            updates.arrayItemProperties = [];
                                        } else {
                                            updates.arrayItemProperties = undefined;
                                        }

                                        updateField(field.id, updates, parentPath);
                                    }}
                                >
                                    <SelectTrigger className="mt-1">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {JSON_TYPES.filter(t => t !== 'null').map((type) => (
                                            <SelectItem key={type} value={type}>
                                                {type}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Default Value */}
                        {field.type !== 'object' && field.type !== 'array' && (
                            <div>
                                <Label className="text-xs">Default Value (optional)</Label>
                                {field.type === 'boolean' ? (
                                    <Select
                                        value={field.defaultValue?.toString() || 'undefined'}
                                        onValueChange={(value) => handleDefaultValueChange(field.id, value, parentPath)}
                                    >
                                        <SelectTrigger className="mt-1">
                                            <SelectValue placeholder="None" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="undefined">None</SelectItem>
                                            <SelectItem value="true">true</SelectItem>
                                            <SelectItem value="false">false</SelectItem>
                                        </SelectContent>
                                    </Select>
                                ) : (
                                    <Input
                                        value={field.defaultValue?.toString() || ''}
                                        onChange={(e) => handleDefaultValueChange(field.id, e.target.value, parentPath)}
                                        placeholder={field.type === 'number' ? '0' : 'Default value'}
                                        type={field.type === 'number' ? 'number' : 'text'}
                                        className="mt-1"
                                    />
                                )}
                            </div>
                        )}

                        {/* Description */}
                        <div>
                            <Label className="text-xs">Description (optional)</Label>
                            <Input
                                value={field.description || ''}
                                onChange={(e) => updateField(field.id, {
                                    description: e.target.value
                                }, parentPath)}
                                placeholder="Field description"
                                className="mt-1"
                            />
                        </div>

                        {/* Required Checkbox */}
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id={`required-${currentPath}`}
                                checked={field.required}
                                onCheckedChange={(checked) => updateField(field.id, {
                                    required: checked === true
                                }, parentPath)}
                            />
                            <Label htmlFor={`required-${currentPath}`} className="text-xs cursor-pointer">
                                Required field
                            </Label>
                        </div>

                        {/* Add Properties button for object type */}
                        {field.type === 'object' && isExpanded && (
                            <div className="pt-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => addField(currentPath)}
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Property
                                </Button>
                            </div>
                        )}

                        {/* Add Properties button for array of objects */}
                        {field.type === 'array' && field.arrayItemType === 'object' && isExpanded && (
                            <div className="pt-2">
                                <Label className="text-xs font-semibold mb-2 block">Array Item Properties</Label>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => addField(currentPath)}
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Item Property
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Render nested fields */}
                    {isExpanded && field.type === 'object' && field.children && field.children.length > 0 && (
                        <div className="space-y-3">
                            {renderFields(field.children, currentPath, depth + 1)}
                        </div>
                    )}

                    {/* Render array item properties */}
                    {isExpanded && field.type === 'array' && field.arrayItemType === 'object' &&
                     field.arrayItemProperties && field.arrayItemProperties.length > 0 && (
                     <div className="space-y-3">
                         {renderFields(field.arrayItemProperties, currentPath, depth + 1)}
                     </div>
                    )}
                </div>
            );
        });
    };

    return (
        <div className="grid grid-cols-2 gap-4 h-full overflow-hidden">
            {/* Left Side - Schema Builder */}
            <div className="flex flex-col h-full overflow-hidden">
                <div className="flex-shrink-0 space-y-4 pb-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-sm font-semibold">{title}</h3>
                        <div className="flex gap-2">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm">
                                        <span>Load Example</span>
                                        <ChevronDown className="ml-2 h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => loadExample('user')}>User Schema</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => loadExample('product')}>Product Schema</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => loadExample('posts')}>Posts Array Schema</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => loadExample('error')}>Error Schema</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => loadExample('articles')}>
                                        Articles (Nested Objects)
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            <Button variant="outline" size="sm" onClick={clearSchema}>
                                Clear
                            </Button>
                        </div>
                    </div>

                    {/* Root Type Selector */}
                    <div className="space-y-2">
                        <Label htmlFor="root-type">Root Type</Label>
                        <Select value={rootType} onValueChange={(value) => setRootType(value as JsonSchemaType)}>
                            <SelectTrigger id="root-type">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="object">Object</SelectItem>
                                <SelectItem value="array">Array</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Fields Section - Scrollable */}
                {(rootType === 'object' || rootType === 'array') && (
                    <div className="flex-1 overflow-y-auto pr-2 space-y-3">
                        <div className="flex justify-between items-center sticky top-0 bg-background z-10 pb-2">
                            <Label>{rootType === 'object' ? 'Properties' : 'Array Item Properties'}</Label>
                            <Button type="button" variant="outline" size="sm" onClick={() => addField()}>
                                <Plus className="h-4 w-4 mr-2" />
                                Add Field
                            </Button>
                        </div>

                        {fields.length === 0 ? (
                            <div className="text-center p-4 border border-dashed rounded-md">
                                <p className="text-sm text-muted-foreground">No fields added yet</p>
                            </div>
                        ) : (
                            <div className="space-y-4 pb-4">
                                {renderFields(fields)}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Right Side - JSON Preview (editable / paste target) */}
            <div className="flex flex-col h-full overflow-hidden">
                <div className="flex items-center justify-between flex-shrink-0 pb-2">
                    <h3 className="text-sm font-semibold">JSON Schema Preview</h3>
                    <span className="text-[11px] text-muted-foreground">
                        Editable — paste a schema or example JSON
                    </span>
                </div>
                <textarea
                    value={editText}
                    onChange={(e) => handlePreviewChange(e.target.value)}
                    spellCheck={false}
                    placeholder='Paste a JSON Schema, or a raw example like {"id": 1, "total": 9.99} and the builder fills in.'
                    className="flex-1 bg-muted rounded-md p-3 overflow-auto text-xs font-mono
                               whitespace-pre resize-none outline-none focus:ring-1 focus:ring-primary"
                />
                {parseError && (
                    <p className="text-[11px] text-destructive flex-shrink-0 pt-1">{parseError}</p>
                )}
            </div>
        </div>
    );
}
